import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Empty, Input, Select, Label, Textarea } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { Plus, Trash2, X, Pencil, Repeat, CheckCircle2, XCircle, Eye, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function statusVariant(s) {
  if (s === "accepted") return "success";
  if (s === "sent") return "primary";
  if (s === "declined" || s === "expired") return "danger";
  return "default";
}

export default function QuotesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const isCustomer = user?.role === "customer";
  const canEdit = !isCustomer;
  const isOwner = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/quotes");
    setItems(data || []);
  };
  useEffect(() => {
    load();
    if (!isCustomer) api.get("/customers").then(({ data }) => setCustomers(data || [])).catch(() => {});
  }, [isCustomer]);

  const onDelete = async (q) => {
    if (!window.confirm(`Delete quote ${q.number}? This removes it from Firestore too.`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Quote deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb={isCustomer ? "My account · Quotes" : "Sales · Quotes"} title="Quotes" subtitle="Proposals & pricing." actions={canEdit && <Button onClick={() => setShowNew(true)} data-testid="new-quote-btn"><Plus className="w-4 h-4" strokeWidth={2}/>New quote</Button>} />
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Number</th>
                <th className="text-left font-semibold px-5 py-3">Customer</th>
                <th className="text-left font-semibold px-5 py-3">Status</th>
                <th className="text-left font-semibold px-5 py-3">Items</th>
                <th className="text-left font-semibold px-5 py-3">Created</th>
                <th className="text-right font-semibold px-5 py-3">Total (inc VAT)</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr
                  key={q.id}
                  className={`border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))] group ${isCustomer ? "cursor-pointer" : ""}`}
                  onClick={isCustomer ? () => setViewing(q) : undefined}
                  data-testid="quote-row"
                >
                  <td className="px-5 py-3 font-mono font-semibold">{q.number}</td>
                  <td className="px-5 py-3">{q.customer_name}</td>
                  <td className="px-5 py-3"><StatusPill variant={statusVariant(q.status)}>{q.status}</StatusPill></td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{q.items?.length || 0} line items{(q.monthly_total || 0) > 0 ? <span className="ml-1.5 text-[hsl(var(--primary))] font-semibold">+ recurring</span> : null}</td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{new Date(q.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right font-mono">
                    {(q.monthly_total || 0) > 0 ? (
                      <div className="leading-tight">
                        <div className="font-semibold">{gbp(q.setup_total || 0)} <span className="text-[10px] text-[hsl(var(--text-muted))] font-normal">setup</span></div>
                        <div className="text-[12px] text-[hsl(var(--primary))] font-semibold">+ {gbp(q.monthly_total)} <span className="text-[10px] text-[hsl(var(--text-muted))] font-normal">/mo</span></div>
                      </div>
                    ) : <span className="font-semibold">{gbp(q.total)}</span>}
                  </td>
                  <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {isCustomer && (
                      <button onClick={() => setViewing(q)} className="opacity-80 hover:opacity-100 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] text-[11px] font-semibold transition-all" title="View quote" data-testid="view-quote-btn">
                        <Eye className="w-3 h-3" strokeWidth={2}/>
                        {q.status === "sent" || q.status === "draft" ? "Review" : "View"}
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => setEditing(q)} className="opacity-60 hover:opacity-100 p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] transition-all" title="Edit quote" data-testid="edit-quote-btn">
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/>
                      </button>
                    )}
                    {isOwner && (
                      <button onClick={() => onDelete(q)} className="opacity-60 hover:opacity-100 p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600 transition-all" title="Delete quote" data-testid="delete-quote-btn">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7}><Empty title="No quotes yet" subtitle={canEdit ? "Click 'New quote' to create one." : ""} /></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {showNew && <QuoteFormModal customers={customers} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <QuoteFormModal quote={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <ViewQuoteModal quote={viewing} onClose={() => setViewing(null)} onResponded={() => { setViewing(null); load(); }} />}
    </div>
  );
}

function ViewQuoteModal({ quote, onClose, onResponded }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null); // "accept" | "decline" | null
  const pending = quote.status === "sent" || quote.status === "draft";
  const alreadyResponded = quote.status === "accepted" || quote.status === "declined";

  const respond = async (action) => {
    setSaving(true);
    try {
      await api.post(`/quotes/${quote.id}/respond`, { action, note: note.trim() || null });
      toast.success(action === "accept" ? "Quote accepted. We'll be in touch shortly." : "Quote declined. Thanks for letting us know.");
      onResponded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit response");
    } finally {
      setSaving(false);
      setConfirming(null);
    }
  };

  const oneOff = (quote.items || []).filter((i) => !i.recurring);
  const monthly = (quote.items || []).filter((i) => i.recurring);

  const lineRow = (it, idx) => (
    <tr key={idx} className="border-t border-[hsl(var(--border))]">
      <td className="px-4 py-2.5 text-[13px]">{it.description}</td>
      <td className="px-4 py-2.5 text-[12px] text-[hsl(var(--text-muted))] text-right font-mono">{it.qty}</td>
      <td className="px-4 py-2.5 text-[12px] text-[hsl(var(--text-muted))] text-right font-mono">{gbp(it.unit_price)}</td>
      <td className="px-4 py-2.5 text-[13px] text-right font-mono font-semibold">{gbp((it.qty || 0) * (it.unit_price || 0))}</td>
    </tr>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="view-quote-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Quote</div>
            <h3 className="text-lg font-semibold font-mono mt-0.5">{quote.number}</h3>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill variant={statusVariant(quote.status)}>{quote.status}</StatusPill>
            <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Metadata */}
          <div className="grid grid-cols-3 gap-3 text-[12px]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">For</div>
              <div className="mt-0.5 font-semibold">{quote.customer_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Issued</div>
              <div className="mt-0.5 font-mono">{new Date(quote.created_at).toLocaleDateString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Valid until</div>
              <div className="mt-0.5 font-mono">{quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : "—"}</div>
            </div>
          </div>

          {/* One-off items */}
          {oneOff.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden">
              <div className="px-4 py-2.5 bg-[hsl(var(--background))] border-b border-[hsl(var(--border))]">
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">One-off / Setup</div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                    <th className="text-left font-semibold px-4 py-2">Description</th>
                    <th className="text-right font-semibold px-4 py-2 w-16">Qty</th>
                    <th className="text-right font-semibold px-4 py-2 w-28">Unit</th>
                    <th className="text-right font-semibold px-4 py-2 w-28">Line total</th>
                  </tr>
                </thead>
                <tbody>{oneOff.map(lineRow)}</tbody>
              </table>
            </div>
          )}

          {/* Monthly items */}
          {monthly.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--primary))]/40 overflow-hidden">
              <div className="px-4 py-2.5 bg-[hsl(var(--primary-muted))] border-b border-[hsl(var(--primary))]/40">
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] font-semibold inline-flex items-center gap-1.5"><Repeat className="w-3 h-3" strokeWidth={2}/>Monthly recurring</div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                    <th className="text-left font-semibold px-4 py-2">Description</th>
                    <th className="text-right font-semibold px-4 py-2 w-16">Qty</th>
                    <th className="text-right font-semibold px-4 py-2 w-28">Unit / mo</th>
                    <th className="text-right font-semibold px-4 py-2 w-28">Line / mo</th>
                  </tr>
                </thead>
                <tbody>{monthly.map(lineRow)}</tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-2">Setup total (inc VAT)</div>
              <div className="font-mono text-2xl font-bold">{gbp(quote.setup_total || quote.total || 0)}</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">VAT @ {Math.round((quote.vat_rate || 0) * 100)}% included</div>
            </div>
            {(quote.monthly_total || 0) > 0 && (
              <div className="rounded-xl border border-[hsl(var(--primary))]/40 p-4 bg-[hsl(var(--primary-muted))]">
                <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] font-semibold mb-2">Monthly (inc VAT)</div>
                <div className="font-mono text-2xl font-bold text-[hsl(var(--primary))]">{gbp(quote.monthly_total)}<span className="text-[13px] font-normal text-[hsl(var(--text-muted))]">/mo</span></div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Recurring until cancelled</div>
              </div>
            )}
          </div>

          {quote.notes && (
            <div className="rounded-xl border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-2">Notes</div>
              <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{quote.notes}</div>
            </div>
          )}

          {/* Response panel */}
          {alreadyResponded && (
            <div className={`rounded-xl border p-4 ${quote.status === "accepted" ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid="quote-response-banner">
              <div className="flex items-start gap-3">
                {quote.status === "accepted" ? <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5" strokeWidth={2}/> : <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 mt-0.5" strokeWidth={2}/>}
                <div className="flex-1 text-[13px]">
                  <div className="font-semibold">
                    {quote.status === "accepted" ? "Quote accepted" : "Quote declined"}
                    {quote.responded_by_name ? ` by ${quote.responded_by_name}` : ""}
                  </div>
                  {quote.responded_at && (
                    <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={2}/>{new Date(quote.responded_at).toLocaleString()}</div>
                  )}
                  {quote.response_note && <div className="mt-2 text-[12px] italic text-[hsl(var(--text-muted))]">"{quote.response_note}"</div>}
                </div>
              </div>
            </div>
          )}

          {pending && (
            <div className="rounded-xl border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))] space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Your response</div>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to Soubor One (questions, timing, conditions…)" data-testid="quote-response-note"/>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2 bg-[hsl(var(--background))]/50">
          <Button type="button" variant="secondary" onClick={onClose}>Close</Button>
          {pending && (
            <>
              <Button type="button" variant="secondary" disabled={saving} onClick={() => setConfirming("decline")} data-testid="decline-quote-btn">
                <XCircle className="w-4 h-4" strokeWidth={2}/>Decline
              </Button>
              <Button type="button" disabled={saving} onClick={() => setConfirming("accept")} data-testid="accept-quote-btn">
                <CheckCircle2 className="w-4 h-4" strokeWidth={2}/>Accept quote
              </Button>
            </>
          )}
        </div>

        {confirming && (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => !saving && setConfirming(null)}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl p-6" data-testid="quote-confirm-modal">
              <h4 className="text-lg font-semibold mb-2">
                {confirming === "accept" ? "Accept this quote?" : "Decline this quote?"}
              </h4>
              <p className="text-[13px] text-[hsl(var(--text-muted))] leading-relaxed mb-4">
                {confirming === "accept"
                  ? `You're accepting ${quote.number}, ${gbp(quote.setup_total || quote.total || 0)} setup${(quote.monthly_total || 0) > 0 ? ` plus ${gbp(quote.monthly_total)}/mo` : ""}. We'll be notified immediately and kick things off.`
                  : `We'll mark ${quote.number} as declined. If you'd like to revisit the pricing or scope, just let us know. Your account manager is always happy to help.`}
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setConfirming(null)} disabled={saving}>Cancel</Button>
                <Button type="button" onClick={() => respond(confirming)} disabled={saving} data-testid="quote-confirm-btn">
                  {saving ? "Submitting…" : confirming === "accept" ? "Yes, accept" : "Yes, decline"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuoteFormModal({ quote, customers, onClose, onSaved }) {
  const isEdit = !!quote;
  const [form, setForm] = useState(() => ({
    customer_id: quote?.customer_id || "",
    customer_name: quote?.customer_name || "",
    status: quote?.status || "draft",
    vat_rate: quote?.vat_rate ?? 0.2,
    valid_until: quote?.valid_until ? quote.valid_until.split("T")[0] : "",
    notes: quote?.notes || "",
    items: quote?.items?.length ? quote.items.map((i) => ({ ...i })) : [{ description: "", qty: 1, unit_price: 0, recurring: false }],
  }));
  const [saving, setSaving] = useState(false);

  const pickCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setForm({ ...form, customer_id: cid, customer_name: c?.company || "" });
  };

  const setItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: k === "description" ? v : k === "recurring" ? v : parseFloat(v) || 0 };
    setForm({ ...form, items });
  };
  const addItem = (recurring = false) => setForm({ ...form, items: [...form.items, { description: "", qty: 1, unit_price: 0, recurring }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const oneOffSub = form.items.filter((it) => !it.recurring).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0);
  const monthlySub = form.items.filter((it) => it.recurring).reduce((s, it) => s + (it.qty || 0) * (it.unit_price || 0), 0);
  const vatRate = parseFloat(form.vat_rate) || 0;
  const oneOffVat = oneOffSub * vatRate;
  const monthlyVat = monthlySub * vatRate;
  const setupTotal = oneOffSub + oneOffVat;
  const monthlyTotal = monthlySub + monthlyVat;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Pick a customer"); return; }
    const validItems = form.items.filter((it) => it.description && it.qty > 0);
    if (validItems.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        customer_name: form.customer_name,
        status: form.status,
        vat_rate: vatRate,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        notes: form.notes,
        items: validItems,
      };
      if (isEdit) {
        await api.patch(`/quotes/${quote.id}`, payload);
        toast.success("Quote updated");
      } else {
        const number = `QT-${Date.now().toString().slice(-5)}`;
        await api.post("/quotes", { ...payload, number });
        toast.success("Quote created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save quote");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-3xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="quote-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? `Edit ${quote.number}` : "New quote"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer *</Label>
              <Select value={form.customer_id} onChange={(e) => pickCustomer(e.target.value)} data-testid="quote-customer">
                <option value="">Select…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </Select>
            </div>
            <div>
              <Label>VAT rate</Label>
              <Select value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) })}>
                <option value="0">0% (no VAT)</option>
                <option value="0.05">5%</option>
                <option value="0.2">20%</option>
              </Select>
            </div>
            <div>
              <Label>Valid until</Label>
              <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}/>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line items *</Label>
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => addItem(false)}><Plus className="w-3.5 h-3.5" strokeWidth={2}/>One-off</Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => addItem(true)}><Repeat className="w-3.5 h-3.5" strokeWidth={2}/>Recurring</Button>
              </div>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid="quote-line">
                  <Input className="col-span-6" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                  <Input className="col-span-1" type="number" min="0" step="1" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="Unit £" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                  <button
                    type="button"
                    onClick={() => setItem(i, "recurring", !it.recurring)}
                    className={`col-span-2 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-semibold border transition-all ${it.recurring ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:border-[hsl(var(--text))]"}`}
                    title={it.recurring ? "Monthly recurring" : "One-off (click to make recurring)"}
                    data-testid="recurring-toggle"
                  >
                    <Repeat className="w-3 h-3" strokeWidth={2}/>{it.recurring ? "Monthly" : "One-off"}
                  </button>
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600 flex items-center justify-center" title="Remove"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                </div>
              ))}
            </div>
          </div>

          <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Scope / assumptions / payment terms…"/></div>

          <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4 text-[13px] grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-1">One-off / setup</div>
              <div className="flex justify-between"><span className="text-[hsl(var(--text-muted))]">Subtotal</span><span className="font-mono">{gbp(oneOffSub)}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(var(--text-muted))]">VAT ({(vatRate * 100).toFixed(0)}%)</span><span className="font-mono">{gbp(oneOffVat)}</span></div>
              <div className="flex justify-between border-t border-[hsl(var(--border))] pt-1"><span className="font-semibold">Setup total</span><span className="font-mono font-bold">{gbp(setupTotal)}</span></div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] font-semibold mb-1">Monthly recurring</div>
              <div className="flex justify-between"><span className="text-[hsl(var(--text-muted))]">Subtotal</span><span className="font-mono">{gbp(monthlySub)}</span></div>
              <div className="flex justify-between"><span className="text-[hsl(var(--text-muted))]">VAT ({(vatRate * 100).toFixed(0)}%)</span><span className="font-mono">{gbp(monthlyVat)}</span></div>
              <div className="flex justify-between border-t border-[hsl(var(--border))] pt-1"><span className="font-semibold">Per month</span><span className="font-mono font-bold text-[hsl(var(--primary))]">{gbp(monthlyTotal)}/mo</span></div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="quote-save-btn">{saving ? "Saving…" : isEdit ? "Save changes" : "Create quote"}</Button>
        </div>
      </form>
    </div>
  );
}
